/**
 * AsyncStorageService
 *
 * Non-blocking wrapper around localStorage that eliminates UI-thread freezes
 * caused by synchronous JSON.stringify + setItem calls.
 *
 * Design:
 *  - In-memory Map acts as a write-ahead cache.
 *  - Writes go to memory instantly; reads check memory first.
 *  - Pending writes are flushed to localStorage via requestIdleCallback
 *    with a 100 ms debounce, batching rapid updates into a single I/O pass.
 *  - A beforeunload listener guarantees data is persisted when the tab closes.
 *
 * API:
 *  set(key, value)      – debounced write  (hot paths, large JSON)
 *  setSync(key, value)  – immediate write  (cross-tab critical: logout)
 *  get(key)             – sync read from memory → localStorage fallback
 *  remove(key)          – immediate remove from memory + localStorage
 *  clear()              – wipe everything
 *  flush()              – force-write all pending data now
 */

import logger from '../logging/logger';

const DEBOUNCE_MS = 100;

class AsyncStorageService {
  constructor() {
    /** @type {Map<string, string>} serialized values ready to read/write */
    this.memoryCache = new Map();

    /** @type {Set<string>} keys waiting to be flushed to localStorage */
    this.pendingWrites = new Set();

    this.flushTimer = null;
    this.idleCallbackId = null;

    // Ensure data survives tab close / navigation
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());
    }
  }

  // ───────────────────── writes ─────────────────────

  /**
   * Debounced write — value is available via get() immediately,
   * but the actual localStorage.setItem happens in the next idle window.
   */
  set(key, value) {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    this.memoryCache.set(key, serialized);
    this.pendingWrites.add(key);
    this._scheduleFlush();
  }

  /**
   * Immediate write — for operations that MUST be visible to other tabs
   * right away (e.g. logout propagation via storage events).
   */
  setSync(key, value) {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    this.memoryCache.set(key, serialized);
    this.pendingWrites.delete(key); // no need to flush later

    try {
      localStorage.setItem(key, serialized);
    } catch (error) {
      this._handleWriteError(key, error);
    }
  }

  // ───────────────────── reads ──────────────────────

  /**
   * Synchronous read — checks memory cache first (always has latest),
   * then falls back to localStorage and populates the cache.
   */
  get(key) {
    if (this.memoryCache.has(key)) {
      return this.memoryCache.get(key);
    }

    try {
      const value = localStorage.getItem(key);
      if (value !== null) {
        this.memoryCache.set(key, value);
      }
      return value;
    } catch (error) {
      logger.error('Failed to read from localStorage', { key, error: error.message });
      return null;
    }
  }

  /**
   * Parse-and-return helper — reads the key, JSON.parses the result,
   * and returns the parsed object (or `fallback` on miss / parse error).
   * Keeps the JSON.parse cost inside one place so callers stay clean.
   */
  getJSON(key, fallback = null) {
    const raw = this.get(key);
    if (raw === null) return fallback;

    try {
      return JSON.parse(raw);
    } catch {
      logger.error('JSON.parse failed for storage key', { key });
      return fallback;
    }
  }

  // ───────────────────── deletes ────────────────────

  /**
   * Immediate remove from both memory and localStorage.
   */
  remove(key) {
    this.memoryCache.delete(key);
    this.pendingWrites.delete(key);

    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error('Failed to remove from localStorage', { key, error: error.message });
    }
  }

  /**
   * Clear all storage managed by this service.
   *
   * Only removes keys that were written through this service (tracked in
   * memoryCache). Keys written directly to localStorage (e.g. ecoh-custom-theme)
   * are intentionally preserved so they survive logout/clear cycles.
   */
  clear() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.idleCallbackId && typeof cancelIdleCallback === 'function') {
      cancelIdleCallback(this.idleCallbackId);
      this.idleCallbackId = null;
    }

    // Remove only keys this service manages — never nuke unrelated localStorage keys
    for (const key of this.memoryCache.keys()) {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        logger.error('Failed to remove key during clear', { key, error: error.message });
      }
    }

    this.memoryCache.clear();
    this.pendingWrites.clear();
  }

  // ───────────────────── flush ──────────────────────

  /**
   * Force-write all pending keys to localStorage synchronously.
   * Called automatically on beforeunload; can also be called manually.
   */
  flush() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.idleCallbackId && typeof cancelIdleCallback === 'function') {
      cancelIdleCallback(this.idleCallbackId);
      this.idleCallbackId = null;
    }

    this._flushToStorage();
  }

  // ───────────────────── internals ──────────────────

  /** @private */
  _scheduleFlush() {
    if (this.flushTimer) return; // already scheduled

    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;

      if (typeof requestIdleCallback === 'function') {
        this.idleCallbackId = requestIdleCallback(() => {
          this.idleCallbackId = null;
          this._flushToStorage();
        });
      } else {
        this._flushToStorage();
      }
    }, DEBOUNCE_MS);
  }

  /** @private */
  _flushToStorage() {
    if (this.pendingWrites.size === 0) return;

    const keys = [...this.pendingWrites];
    this.pendingWrites.clear();

    for (const key of keys) {
      const value = this.memoryCache.get(key);
      if (value === undefined) continue; // removed between schedule and flush

      try {
        localStorage.setItem(key, value);
      } catch (error) {
        this._handleWriteError(key, error);
      }
    }
  }

  /** @private */
  _handleWriteError(key, error) {
    if (error.name === 'QuotaExceededError') {
      logger.error('localStorage quota exceeded', { key });
    } else {
      logger.error('Failed to write to localStorage', { key, error: error.message });
    }
  }
}

export const asyncStorage = new AsyncStorageService();
export default asyncStorage;
